import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { ConfirmationModalComponent } from 'commons/components';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { usePocQuery } from 'core/custom-hooks/use-poc-query.hook';
import { useTranslation } from 'core/i18n/i18n';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { updateQuery } from 'core/query-client/query-client';
import { executeRequest } from 'core/utils/backend-service';
import { showSuccessNotification } from 'core/utils/notification-service';
import { SettingsViewContent, SettingsViewHeader, SettingsViewLayout } from 'modules/settings/settings.style';
import { useEffect, useState } from 'react';
import { usePopupManager } from 'react-popup-manager';
import { applyTrainingRegistrations, getExistingTrainingRegistrations } from './service/lms-api-integration.service';
import { useTrainingRegistrationMapping } from './commons/hooks/use-training-registration-mapping.hook';
import { SettingsIntegrationsLmsApiIntegrationMapping } from './settings-integrations-lms-api-integration-mapping.component';
import { SettingsIntegrationsLmsApiIntegrationTrainingRegistration } from './settings-integrations-lms-api-integration-training-registration.component';
import { TrainingRegistration, newTrainingRegistration } from './types/lms-training-registration.type';

export const SettingsLmsApiIntegrationContainer: React.FC = () => {
    const [trainingRegistrations, setTrainingRegistrations] = useState<TrainingRegistration[]>([]);

    const { t } = useTranslation();
    const popupManager = usePopupManager();

    const { data: loadedTrainingRegistrations, isFetching: loadingTrainingsRegistrations } = usePocQuery<
        TrainingRegistration[]
    >({
        queryKey: settingsKeys.lmsTrainingRegistrations(),
        queryFn: () => executeRequest(() => getExistingTrainingRegistrations()),
    });

    // Seed the locally-editable registrations list from the query (rows are added/edited/removed before save).
    useEffect(() => {
        if (loadedTrainingRegistrations) {
            setTrainingRegistrations(loadedTrainingRegistrations);
        }
    }, [loadedTrainingRegistrations]);

    const { data: trainingRegistrationMappingData, isFetching: loading } = useTrainingRegistrationMapping();

    /* BLOCK TO TRAINING REGISTRATION */

    const handleAddTrainingRegistration = () => {
        const updatedTrainingRegistration = [
            ...trainingRegistrations,
            newTrainingRegistration(Math.max(...trainingRegistrations.map((tr) => tr.index), 0) + 1),
        ];

        setTrainingRegistrations(updatedTrainingRegistration);
    };

    const handleChangeTrainingRegistration = (trainingRegistration: TrainingRegistration) => {
        const updatedTrainingRegistration = [...trainingRegistrations];
        const index = trainingRegistrations.findIndex((tr) => tr.index === trainingRegistration.index);
        updatedTrainingRegistration.splice(index, 1, trainingRegistration);

        setTrainingRegistrations(updatedTrainingRegistration);
    };

    const handleRemoveTrainingRegistration = (trainingRegistration: TrainingRegistration) => {
        const updatedTrainingRegistration = trainingRegistrations.filter(
            (tr) => tr.index !== trainingRegistration.index
        );

        setTrainingRegistrations(updatedTrainingRegistration);
    };

    const { mutateAsync: applyTrainingRegistrationsMutation } = usePocMutation({
        mutationFn: (registrations: TrainingRegistration[]) =>
            executeRequest(() => applyTrainingRegistrations(registrations)),
        onSuccess: () => {
            showSuccessNotification(
                t('settings.integrations.lms-api-integration.training-registration.save.notification.positive')
            );
            updateQuery(settingsKeys.lmsTrainingRegistrations());
        },
    });

    const saveTrainingRegistrations = () => applyTrainingRegistrationsMutation(trainingRegistrations);

    const handleApplyTrainingRegistration = () => {
        popupManager.open(ConfirmationModalComponent, {
            title: t('settings.integrations.lms-api-integration.training-registration.save.confirmation-modal.title'),
            children: t(
                'settings.integrations.lms-api-integration.training-registration.save.confirmation-modal.description'
            ),
            buttonText: t(
                'settings.integrations.lms-api-integration.training-registration.save.confirmation-modal.button'
            ),
            actionOnClick: saveTrainingRegistrations,
        });
    };

    /* END BLOCK TO TRAINING REGISTRATION */

    /* BLOCK TO MAPPING */

    const refreshTrainingRegistrationMapping = () => updateQuery(settingsKeys.lmsMapping());

    /* END BLOCK TO MAPPING */

    return (
        <SettingsViewLayout>
            <SettingsViewHeader>
                <PocTypography element="h5" variant="title5">
                    {t('settings.integrations.lms-api-integration.title')}
                </PocTypography>
            </SettingsViewHeader>
            <SettingsViewContent>
                <SettingsIntegrationsLmsApiIntegrationTrainingRegistration
                    trainingRegistrations={trainingRegistrations}
                    loading={loadingTrainingsRegistrations}
                    onAddTrainingRegistration={handleAddTrainingRegistration}
                    onChangeTrainingRegistration={handleChangeTrainingRegistration}
                    onRemoveTrainingRegistration={handleRemoveTrainingRegistration}
                    onApplyTrainingRegistration={handleApplyTrainingRegistration}
                />
                <SettingsIntegrationsLmsApiIntegrationMapping
                    lmsIntegrationMappingData={trainingRegistrationMappingData}
                    loading={loading}
                    onSaveMappings={refreshTrainingRegistrationMapping}
                />
            </SettingsViewContent>
        </SettingsViewLayout>
    );
};
